import { useGetCurrentUser } from "@services";
import { useCrossTabSessionSync, useHydrateSession } from "@store/session";
import { store } from "@store/store";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useEffect } from "react";
import { Provider as ReduxProvider } from "react-redux";
import { RouterProvider } from "react-router/dom";
import { detectRefreshRageOnLoad } from "@/analytics/refreshRageDetector";
import { useFirebaseSilentAuth } from "@/auth/silentReauth";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/ui/theme-provider";
import {
  isBuildReloadSafe,
  subscribeBuildReloadSafety,
} from "@/services/buildReloadSafety";
import { isFunctionValue } from "@/utils/typePredicates";
import { queryClient } from "./queryClient";
import { router } from "./router";
import "./index.css";
import { IndexedDBErrorBoundary } from "@bootstrap";
import { safeConsole } from "@utils";
import { createStaleBuildMonitor } from "./router/staleBuildReload";

// LocalStorage cleanup to prevent bloat
const STORAGE_CLEANUP_KEY = "last-storage-cleanup";
const STORAGE_CLEANUP_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days

const cleanupOldStorageEntries = (): void => {
  try {
    const lastCleanup = localStorage.getItem(STORAGE_CLEANUP_KEY);
    const now = Date.now();

    // Only run cleanup if it hasn't been done in the last week
    if (
      !lastCleanup ||
      now - parseInt(lastCleanup, 10) > STORAGE_CLEANUP_INTERVAL
    ) {
      const keysToRemove: string[] = [];

      // Remove any keys that might be session-related but orphaned
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        // Remove temporary or session-related entries
        if (
          key.startsWith("temp-") ||
          key.startsWith("cache-") ||
          key.includes("timestamp") ||
          (key.includes("session") && key !== "session_id")
        ) {
          keysToRemove.push(key);
        }
      }

      // Remove identified keys
      keysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          safeConsole.warn(
            "local-storage",
            `Failed to remove localStorage key: ${key}`,
            error,
          );
        }
      });

      // Update cleanup timestamp
      localStorage.setItem(STORAGE_CLEANUP_KEY, now.toString());

      if (keysToRemove.length > 0) {
        safeConsole.log(
          "local-storage",
          `Cleaned up ${keysToRemove.length} old localStorage entries`,
        );
      }
    }
  } catch (error) {
    safeConsole.warn("local-storage", "localStorage cleanup failed:", error);
  }
};

const App = () => {
  useHydrateSession();
  useCrossTabSessionSync();
  useFirebaseSilentAuth();
  useGetCurrentUser();

  useEffect(() => {
    // Run localStorage cleanup on app startup
    cleanupOldStorageEntries();

    // Reload-rage detection (SD-10021): runs once per page load. A triggered
    // rage is stored as a pending marker and captured by the gating hook once
    // the SDK opts in (a capture here would fire while still opted out).
    detectRefreshRageOnLoad();

    // Drop the retired navigation counter (SD-10536): the 50-navigation
    // forced reload is removed, so clear its stale key for existing users.
    try {
      localStorage.removeItem("app-navigation-count");
    } catch {
      // localStorage unavailable (private browsing / disabled) — nothing to do
    }

    // Stale-build guard (SD-10488): when the tab regains visibility, ask
    // /version.json (throttled) whether a newer build was deployed; if so,
    // reload immediately when idle or defer until the final live call ends.
    // Old chunks stay downloadable for 30 days (SD-10437), so a stale tab keeps
    // working while blocked without cutting call audio.
    const staleBuildMonitor = createStaleBuildMonitor({
      isReloadSafe: isBuildReloadSafe,
    });
    const checkBuildVersion = () => {
      if (import.meta.env.DEV) return;
      if (document.visibilityState !== "visible") return;
      void staleBuildMonitor.checkNow();
    };
    window.addEventListener("focus", checkBuildVersion);
    document.addEventListener("visibilitychange", checkBuildVersion);

    // Navigation is an extra retry boundary when a prior reload was blocked.
    // The final call blocker releasing also retries immediately.
    const handleNavigation = () => {
      staleBuildMonitor.reloadIfStale();
    };
    const unsubscribeBuildReloadSafety = subscribeBuildReloadSafety(() => {
      staleBuildMonitor.reloadIfStale();
    });

    // Listen for browser back/forward navigation
    window.addEventListener("popstate", handleNavigation);

    // Listen to programmatic router navigations, including query/hash-only
    // changes (filters, pagination).
    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = router.subscribe?.(() => {
        handleNavigation();
      });
    } catch (error) {
      safeConsole.warn(
        "application-lifecycle",
        "Router subscription failed:",
        error,
      );
    }

    // The legacy module-fetch service worker is retired (SD-10507):
    // public/sw.js is now a self-destructing tombstone, so no registration or
    // update-promotion wiring remains here. Previously-controlled clients pick
    // the tombstone up on the browser's automatic update check and come back
    // worker-free; chunk failures surface to the router's recovery path.

    // Cleanup on unmount
    return () => {
      window.removeEventListener("focus", checkBuildVersion);
      document.removeEventListener("visibilitychange", checkBuildVersion);
      window.removeEventListener("popstate", handleNavigation);
      unsubscribeBuildReloadSafety();
      if (unsubscribe && isFunctionValue(unsubscribe)) {
        try {
          unsubscribe();
        } catch (error) {
          safeConsole.warn(
            "application-lifecycle",
            "Router unsubscribe failed:",
            error,
          );
        }
      }
    };
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <Toaster />
    </>
  );
};

const Providers = () => (
  <StrictMode>
    <ThemeProvider defaultTheme="system">
      <QueryClientProvider client={queryClient}>
        <ReduxProvider store={store}>
          <IndexedDBErrorBoundary>
            <App />
          </IndexedDBErrorBoundary>
        </ReduxProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>
);

export default Providers;
